import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Stack,
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Coffee,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useNotification } from "../../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../../Hooks/useApiErrorHandler";
import httpclient from "../../../../../Utils";
import useTokenRefresh from "../../../../../Hooks/useTokenRefresh";
import CustomLoadingOverlay from "../../../../../Components/CustomLoadingOverlay";

const BreakLogRow = ({ log, index }) => {
  const [open, setOpen] = useState(false);
  const [detailsPage, setDetailsPage] = useState(1);
  const detailsPerPage = 10;

  const breakDetails = log.break_details || [];
  const totalDetailsPages = Math.ceil(breakDetails.length / detailsPerPage);
  const startIndex = (detailsPage - 1) * detailsPerPage;
  const endIndex = startIndex + detailsPerPage;
  const paginatedDetails = breakDetails.slice(startIndex, endIndex);

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{log.date}</TableCell>
        <TableCell align="center">{log.break}</TableCell>
        <TableCell align="center">{log.work}</TableCell>
        <TableCell align="center">{log.work_start}</TableCell>
        <TableCell align="center">{log.work_end}</TableCell>
        <TableCell align="center">
          <Chip label={log.total_break} color="primary" size="small" />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                Break Details
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Break #</TableCell>
                    <TableCell>Start Time</TableCell>
                    <TableCell>End Time</TableCell>
                    <TableCell>Duration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedDetails.length > 0 ? (
                    paginatedDetails.map((detail, idx) => (
                      <TableRow key={detail.id}>
                        <TableCell>{startIndex + idx + 1}</TableCell>
                        <TableCell>
                          {detail.start_date
                            ? dayjs(detail.start_date).format(
                                "DD/MM/YYYY hh:mm A"
                              )
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          {detail.end_date
                            ? dayjs(detail.end_date).format(
                                "DD/MM/YYYY hh:mm A"
                              )
                            : "Ongoing"}
                        </TableCell>
                        <TableCell>{detail.duration || "N/A"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No break details available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {totalDetailsPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing {startIndex + 1} to{" "}
                    {Math.min(endIndex, breakDetails.length)} of{" "}
                    {breakDetails.length} breaks
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={detailsPage === 1}
                      onClick={() => setDetailsPage(detailsPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={detailsPage >= totalDetailsPages}
                      onClick={() => setDetailsPage(detailsPage + 1)}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const BreakDetailsDialog = ({ open, onClose, selectedRow }) => {
  const [breakData, setBreakData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  const fetchBreakDetails = async () => {
    if (!selectedRow?.picker_id || !selectedRow?.date) return;

    setLoading(true);
    try {
      let url = `users/${selectedRow.picker_id}/break-details?request_date=${selectedRow.date}&page=${page}&pagination=${pagination}`;

      const res = await httpclient.get(url);
      const { data } = res;

      if (data.status === 200) {
        setBreakData(data.data);
        setTotalRecords(data.meta?.total || 0);
      } else {
        showNotification(
          "error",
          data.message || "Failed to fetch break details"
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && selectedRow?.picker_id && selectedRow?.date) {
      fetchBreakDetails();
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedRow]);

  useEffect(() => {
    if (open && selectedRow?.picker_id && selectedRow?.date) {
      fetchBreakDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pagination]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
    setPage(1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: (theme) => theme.shadows[10],
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Coffee color="primary" />
          <Typography variant="h6" fontWeight="700" color="text.primary">
            Break Report Details
          </Typography>
          {selectedRow?.picker && (
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              - {selectedRow.picker}
            </Typography>
          )}
        </Stack>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ p: 3, paddingTop: "24px !important", bgcolor: "#f8f9fa" }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CustomLoadingOverlay />
          </Box>
        ) : breakData.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Date</TableCell>
                  <TableCell align="center">Break</TableCell>
                  <TableCell align="center">Work</TableCell>
                  <TableCell align="center">Work Start</TableCell>
                  <TableCell align="center">Work End</TableCell>
                  <TableCell align="center">Total Breaks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {breakData.map((log, index) => (
                  <BreakLogRow
                    key={`${log.picker_id}-${log.date}`}
                    log={log}
                    index={index}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No break details available for this picker.
            </Typography>
          </Box>
        )}

        {/* Pagination Controls */}
        {totalRecords > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {(page - 1) * pagination + 1} to{" "}
              {Math.min(page * pagination, totalRecords)} of {totalRecords}{" "}
              records
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={page * pagination >= totalRecords}
                onClick={() => handlePageChange(page + 1)}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          disableElevation
          sx={{ px: 4, borderRadius: 2 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BreakDetailsDialog;
